import React, { useEffect, useState } from 'react';
import { LayoutDashboard, PenTool, Archive, Settings, LogOut, User as UserIcon, Moon, Sun, Shield } from 'lucide-react';
import { ORG_NAME } from '../constants';
import { supabase } from '../services/supabaseClient';

interface SidebarProps {
  currentView: string;
  onChangeView: (view: string) => void;
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, onChangeView, isDarkMode, toggleTheme }) => {
  const [profile, setProfile] = useState<{name: string, role: string, avatar: string} | null>(null);

  useEffect(() => {
    const getProfile = async () => {
      // 1. Get the authenticated user's ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // 2. Fetch the corresponding record from the 'profiles' table
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error("Error fetching profile:", error.message);
        }

        if (data) {
          // 3. Map the database columns to the sidebar state
          setProfile({
            name: data.full_name || user.email || 'User',
            role: data.role || 'journalist', // This correctly sets 'admin' if present in DB
            avatar: data.avatar_url || `https://ui-avatars.com/api/?name=${user.email}`
          });
        } else {
          // Fallback if no profile record exists yet
          setProfile({
            name: user.email || 'User',
            role: 'journalist',
            avatar: `https://ui-avatars.com/api/?name=${user.email}`
          });
        }
      }
    };
    getProfile();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const navItems = [
    { id: 'queue', label: 'My Queue', icon: LayoutDashboard },
    { id: 'new_story', label: 'New Story', icon: PenTool },
    { id: 'archive', label: 'Archive', icon: Archive },
    { id: 'admin', label: 'Admin', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="w-64 bg-slate-900 dark:bg-slate-950 text-slate-100 flex flex-col h-full border-r border-slate-800 transition-colors duration-200">
      <div className="p-6">
        <h1 className="text-xl font-bold tracking-tight text-white">{ORG_NAME} <span className="font-light text-slate-400">OS</span></h1>
        <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Editorial Platform</p>
      </div>

      <div className="px-3 py-2 flex-1">
        <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Navigation</p>
        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            
            // Only show Admin tab to admins
            if (item.id === 'admin' && profile?.role !== 'admin') return null;

            return (
              <button
                key={item.id}
                onClick={() => onChangeView(item.id)}
                className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive 
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-900/20' 
                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100'
                }`}
              >
                <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`} />
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="p-4 border-t border-slate-800">
        {/* Theme Toggle */}
        <button 
          onClick={toggleTheme}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg mb-4 transition-colors"
        >
          <span className="flex items-center">
            {isDarkMode ? <Moon className="h-4 w-4 mr-2" /> : <Sun className="h-4 w-4 mr-2" />}
            {isDarkMode ? 'Dark Mode' : 'Light Mode'}
          </span>
          <div className={`w-8 h-4 rounded-full relative transition-colors ${isDarkMode ? 'bg-blue-600' : 'bg-slate-600'}`}>
            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-200 ${isDarkMode ? 'left-4.5 translate-x-4' : 'left-0.5'}`} />
          </div>
        </button>

        {profile && (
          <div className="flex items-center pt-2">
            <div className="flex-shrink-0">
               <div className="h-9 w-9 rounded-full bg-slate-700 ring-2 ring-slate-800 flex items-center justify-center overflow-hidden">
                  <img src={profile.avatar} alt="User" className="h-full w-full object-cover opacity-80" />
               </div>
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-white truncate">{profile.name}</p>
              <p className="text-xs text-slate-500 capitalize truncate">{profile.role}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="ml-auto text-slate-500 hover:text-white transition-colors"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;